import type { MySurveyFieldsFragment, SurveyAnswerInput } from '@paintscout/api'
import type { SurveyQuestion } from '@paintscout/util/engagement'
import { getRatingScale, validateSurveyAnswers } from '@paintscout/util/engagement'

export type SurveyQuestionView = MySurveyFieldsFragment['questions'][number]

/** What the user has entered for one question so far. */
export interface DraftAnswer {
  choices?: string[]
  rating?: number
  text?: string
}

/** Draft answers by question id. */
export type SurveyDraft = Record<string, DraftAnswer>

/** The draft after the user picks or unpicks an option. A single-choice question keeps one. */
export function setChoice(
  draft: SurveyDraft,
  question: SurveyQuestionView,
  value: string,
  checked: boolean
): SurveyDraft {
  const current = draft[question.id]?.choices ?? []
  let choices: string[]
  if (question.type === 'singleChoice') {
    choices = checked ? [value] : []
  } else {
    choices = checked ? Array.from(new Set([...current, value])) : current.filter((c) => c !== value)
  }
  return { ...draft, [question.id]: { choices } }
}

export function setRating(draft: SurveyDraft, question: SurveyQuestionView, rating: number): SurveyDraft {
  return { ...draft, [question.id]: { rating } }
}

export function setText(draft: SurveyDraft, question: SurveyQuestionView, text: string): SurveyDraft {
  return { ...draft, [question.id]: { text } }
}

/** The whole numbers a rating question offers, low to high. */
export function ratingValues(question: SurveyQuestionView): number[] {
  const { min, max } = getRatingScale(question)
  const values: number[] = []
  for (let value = min; value <= max; value++) values.push(value)
  return values
}

/** Only the value field the question type uses, so a stale field never reaches the API. */
function toInput(question: SurveyQuestionView, answer: DraftAnswer | undefined): SurveyAnswerInput | null {
  if (!answer) return null
  switch (question.type) {
    case 'singleChoice':
    case 'multipleChoice':
      return answer.choices?.length ? { questionId: question.id, choices: answer.choices } : null
    case 'rating':
      return answer.rating != null ? { questionId: question.id, rating: answer.rating } : null
    case 'text':
      return answer.text?.trim() ? { questionId: question.id, text: answer.text } : null
    default:
      return null
  }
}

export interface CheckedDraft {
  valid: boolean
  /** What to send to `submitSurveyResponse`. Empty when there are errors. */
  answers: SurveyAnswerInput[]
  /** Question ids with a problem, each with the message to show under it. */
  errors: Record<string, string>
}

/**
 * Checks the draft with the validator the API runs (`validateSurveyAnswers`), so the dialog
 * rejects exactly what the server would.
 */
export function checkDraft(questions: readonly SurveyQuestionView[], draft: SurveyDraft): CheckedDraft {
  const inputs = questions.map((q) => toInput(q, draft[q.id])).filter((a): a is SurveyAnswerInput => !!a)
  const { errors } = validateSurveyAnswers(
    // The fragment is SurveyQuestion without `hubspotProperty` (the API drops it), and its `type` is
    // the generated enum of the same strings, so the shapes match field for field.
    questions as SurveyQuestion[],
    inputs.map((a) => ({
      questionId: a.questionId,
      choices: a.choices ?? undefined,
      rating: a.rating ?? undefined,
      text: a.text ?? undefined
    }))
  )
  const byQuestion: Record<string, string> = {}
  for (const error of errors) {
    const question = questions.find((q) => error.startsWith(`${q.id}:`))
    if (!question || byQuestion[question.id]) continue
    byQuestion[question.id] = error.endsWith('an answer is required')
      ? 'Please answer this question.'
      : error.slice(question.id.length + 1).trim()
  }
  return { valid: errors.length === 0, answers: errors.length ? [] : inputs, errors: byQuestion }
}
